/**
 * Request detail (design-spec/05 + ux-flows §1.E) — one screen that fans out by state (DESIGN-NOTES
 * delta d: the ledger row links here, and here decides what to show):
 *  - the referee identity + the status timeline (always);
 *  - resend (48h-cooldown-gated, disabled with an explanation) + withdraw for in-flight requests;
 *  - the UnlockPanel (real dual-path unlock — free/referral one-tap + Stripe checkout) for a
 *    Ready·Locked reference, and the seal ceremony + Stripe return handling around it (WP-F11a);
 *  - the LetterViewer for an unlocked reference (the letter TEXT + the Export-PDF presigned download
 *    are both real content reads);
 *  - an empathetic branch for declined.
 *
 * Resend/cancel + the unlock/content/PDF rails all hit the real endpoints. Errors are surfaced calmly,
 * never as a dead end.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, CandidateApiError } from '@/lib/api-client';
import { avatarKindFor, initialsOf, type LedgerCard } from '@/lib/ledger';
import { canCancel, firstNameOf, laterIso, resendGate, untilPhrase } from '@/lib/timeline';
import { clearPendingUnlock, parseCheckoutReturn } from '@/lib/unlock';
import { fetchPdfBlob, saveBlob } from '@/lib/download';
import { AppShell } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/ledger/Avatar';
import { ThreadMark } from '@/components/ThreadMark';
import { StatusTimeline } from '@/components/detail/StatusTimeline';
import { UnlockPanel } from '@/components/detail/UnlockPanel';
import { LetterViewer } from '@/components/letter/LetterViewer';

function BackLink() {
  return (
    <Link
      to="/"
      className="-ml-1 inline-flex items-center gap-1 rounded-md px-1 py-1 font-body text-[13px] font-medium text-ink-quiet transition-colors duration-ui ease-thread hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25"
    >
      ‹ References Ledger
    </Link>
  );
}

function RefereeHeader({ card }: { card: LedgerCard }) {
  return (
    <div className="mt-2 flex items-center gap-3">
      <Avatar initials={initialsOf(card.refereeName)} kind={avatarKindFor(card.state)} size={44} />
      <div className="min-w-0">
        <h1 className="truncate font-display text-[19px] font-semibold tracking-[-0.02em] text-ink">
          {card.refereeName}
        </h1>
        {card.company && <p className="truncate font-body text-[13px] text-ink-quiet">{card.company}</p>}
      </div>
    </div>
  );
}

/** Loading + error placeholders, reusing the ledger's calm patterns (never a dead spinner). */
function DetailLoading() {
  return (
    <AppShell title="Reference">
      <div className="flex flex-1 flex-col items-center justify-center py-20" aria-live="polite" aria-busy="true">
        <ThreadMark className="brand-pulse h-6 w-auto" />
        <p className="mt-3 font-body text-[13px] text-ink-quiet">Loading…</p>
      </div>
    </AppShell>
  );
}

function DetailError({ notFound, onRetry }: { notFound: boolean; onRetry: () => void }) {
  return (
    <AppShell title="Reference">
      <div className="flex flex-1 flex-col items-center justify-center py-20 text-center" role="alert">
        <h2 className="font-display text-[18px] font-semibold tracking-[-0.02em] text-ink">
          {notFound ? 'We couldn’t find that request' : 'We couldn’t load that request'}
        </h2>
        <p className="mt-2 max-w-[32ch] font-body text-[14px] leading-relaxed text-ink-quiet">
          {notFound
            ? 'It may have been withdrawn, or the link is out of date.'
            : 'Something went wrong. Check your connection and try again.'}
        </p>
        <div className="mt-5 flex gap-2">
          {!notFound && (
            <Button variant="ghost" onClick={onRetry}>
              Try again
            </Button>
          )}
          <Button asChild variant={notFound ? 'default' : 'ghost'}>
            <Link to="/">Back to your ledger</Link>
          </Button>
        </div>
      </div>
    </AppShell>
  );
}

/** A surfaced action outcome: warm confirmations render calm (sage/status), failures alert (coral). */
type Notice = { text: string; tone: 'ok' | 'error' };

/** The resend + withdraw controls for an in-flight request. */
function RequestActions({ card, onChanged }: { card: LedgerCard; onChanged: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<Notice | null>(null);
  // A successful resend never moves the wire's sentAt (the server tracks a separate last-invite
  // time), so the cooldown gate is computed from max(sentAt, this client-recorded resend moment) —
  // otherwise the button would re-enable the instant a resend succeeded and the next click would 429.
  const [lastResendAt, setLastResendAt] = useState<string | null>(null);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const first = firstNameOf(card.refereeName) === 'they' ? 'them' : firstNameOf(card.refereeName);
  const gate = resendGate(card.state, laterIso(card.sentAt, lastResendAt));

  const resend = useMutation({
    mutationFn: () => apiClient.resendRequest(card.id),
    onSuccess: () => {
      setLastResendAt(new Date().toISOString());
      setNotice({ text: `We’ve nudged ${first}.`, tone: 'ok' });
      onChanged();
    },
    onError: (err) => {
      if (err instanceof CandidateApiError && err.code === 'resend_too_soon') {
        const when =
          err.retryAfterSeconds !== undefined
            ? `in ${untilPhrase(new Date(Date.now() + err.retryAfterSeconds * 1000))}`
            : 'a little later';
        setNotice({ text: `Not just yet — you can nudge ${first} again ${when}.`, tone: 'error' });
      } else if (err instanceof CandidateApiError && err.code === 'not_resendable') {
        setNotice({ text: 'This request has already moved on.', tone: 'error' });
      } else {
        setNotice({ text: 'We couldn’t resend just now. Try again in a moment.', tone: 'error' });
      }
    },
  });

  const cancel = useMutation({
    mutationFn: () => apiClient.cancelRequest(card.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['ledger'] });
      navigate('/', { replace: true });
    },
    onError: (err) => {
      if (err instanceof CandidateApiError && err.code === 'cannot_cancel') {
        setNotice({ text: `Too late to withdraw — ${first} has already started.`, tone: 'error' });
      } else {
        setNotice({ text: 'We couldn’t withdraw that just now. Try again in a moment.', tone: 'error' });
      }
      setConfirmingCancel(false);
    },
  });

  const showResend = gate.applicable;
  const showCancel = canCancel(card.state);
  if (!showResend && !showCancel) return null;

  return (
    <section className="mt-6 border-t border-line pt-5">
      {notice &&
        (notice.tone === 'error' ? (
          <p role="alert" className="mb-3 rounded-md bg-coral-tint px-3.5 py-2.5 font-body text-[12.5px] text-coral-text">
            {notice.text}
          </p>
        ) : (
          <p role="status" className="mb-3 rounded-md bg-sage-tint px-3.5 py-2.5 font-body text-[12.5px] text-sage-text">
            {notice.text}
          </p>
        ))}

      {showResend && (
        <div>
          <Button
            variant="secondary"
            size="block"
            disabled={!gate.open || resend.isPending}
            onClick={() => resend.mutate()}
          >
            {resend.isPending ? 'Sending…' : `Nudge ${first} again`}
          </Button>
          {!gate.open && gate.availableAt && (
            <p className="mt-1.5 text-center font-body text-[11.5px] text-ink-quiet">
              You can nudge {first} again in {untilPhrase(gate.availableAt)}. A gentle reminder lands better with space.
            </p>
          )}
        </div>
      )}

      {showCancel &&
        (confirmingCancel ? (
          <div className="mt-3 rounded-md border border-line-2 bg-surface p-3.5">
            <p className="font-body text-[13px] text-ink">
              Withdraw this request? {first} won’t be contacted again.
            </p>
            <div className="mt-3 flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setConfirmingCancel(false)}>
                Keep it
              </Button>
              <Button variant="coral" size="sm" disabled={cancel.isPending} onClick={() => cancel.mutate()}>
                {cancel.isPending ? 'Withdrawing…' : 'Withdraw'}
              </Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              setNotice(null);
              setConfirmingCancel(true);
            }}
            className="mt-3 w-full rounded-md px-3 py-2 text-center font-body text-[12.5px] font-medium text-ink-quiet transition-colors duration-ui ease-thread hover:text-coral-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25"
          >
            Withdraw this request
          </button>
        ))}
    </section>
  );
}

/** The empathetic declined branch (ux-flows §1.E — never guilt, one warm next step). */
function DeclinedBranch({ card }: { card: LedgerCard }) {
  const first = firstNameOf(card.refereeName) === 'they' ? 'them' : firstNameOf(card.refereeName);
  return (
    <section className="mt-6 rounded-lg border border-line bg-surface p-5 text-center">
      <p className="font-body text-[14px] leading-relaxed text-ink">
        {first} couldn’t this time. That happens — plenty of people will be glad to help.
      </p>
      <Button asChild className="mt-4">
        <Link to="/request">Ask someone else</Link>
      </Button>
    </section>
  );
}

/**
 * The expired branch. Expired is terminal — the server will not resend an invite past its life (409
 * not_resendable), so there is no nudge to offer. The honest recovery is a fresh request, the same
 * warm next step the declined branch and the mobile app both give.
 */
function ExpiredBranch({ card }: { card: LedgerCard }) {
  const first = firstNameOf(card.refereeName) === 'they' ? 'them' : firstNameOf(card.refereeName);
  return (
    <section className="mt-6 rounded-lg border border-line bg-surface p-5 text-center">
      <p className="font-body text-[14px] leading-relaxed text-ink">
        This invite expired before {first} could reply. No harm done — you can ask someone else whenever you’re ready.
      </p>
      <Button asChild className="mt-4">
        <Link to="/request">Ask someone else</Link>
      </Button>
    </section>
  );
}

/** Unlocked but the letter is not signed yet — the server's "authorized, content pending" state. */
function LetterPending() {
  return (
    <section className="mt-5 rounded-lg border border-line bg-surface p-5 text-center">
      <p className="font-body text-[14px] leading-relaxed text-ink">
        You’re all set — the signed letter is being finalised. It’ll appear here in a moment.
      </p>
    </section>
  );
}

/** Returned from Stripe having cancelled — reassure, never scold, and leave the unlock offer intact. */
function CheckoutCancelledNotice() {
  return (
    <p
      role="status"
      className="mb-4 rounded-md border border-line bg-surface-tint px-3.5 py-2.5 text-center font-body text-[12.5px] text-ink"
    >
      No charge was made. You can unlock whenever you’re ready.
    </p>
  );
}

/** Returned from a successful Stripe checkout, waiting for the webhook to grant the entitlement. */
function ConfirmingPayment({ slow, onRefresh }: { slow: boolean; onRefresh: () => void }) {
  return (
    <section className="mt-5 rounded-xl border border-line bg-surface p-5 text-center" aria-live="polite" aria-busy={!slow}>
      <div className="flex justify-center">
        <ThreadMark className="brand-pulse h-5 w-auto" />
      </div>
      <p className="mt-3 font-body text-[13.5px] leading-relaxed text-ink">
        {slow
          ? 'Still confirming your payment — this can take a moment. It’ll unlock automatically; refresh to check.'
          : 'Confirming your payment — your letter unlocks in just a moment.'}
      </p>
      {slow && (
        <Button variant="ghost" className="mt-3" onClick={onRefresh}>
          Refresh
        </Button>
      )}
    </section>
  );
}

/**
 * The letter / unlock surface for ready + unlocked references (WP-F11a — real content + unlock rails).
 * `getReference(card)` composes the free key-points teaser, the entitlement (from the unlock-options
 * endpoint), and the gated letter body when unlocked. This screen owns the unlock CHOREOGRAPHY:
 *  - locked → the {@link UnlockPanel} (free/referral one-tap + Stripe checkout redirect);
 *  - a locked → unlocked transition → the seal ceremony + a live-region announcement, then the letter;
 *  - the Stripe RETURN (`?checkout=success|cancelled`): poll for the webhook grant on success, a calm
 *    notice on cancel, and the URL param stripped once handled so a reload never replays it.
 */
function ReferenceSurface({ id, card }: { id: string; card: LedgerCard }) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const checkoutReturn = parseCheckoutReturn(searchParams.get('checkout'));
  const awaitingWebhook = checkoutReturn === 'success';

  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfNotice, setPdfNotice] = useState<string | null>(null);
  const [sealing, setSealing] = useState(false);
  const [announce, setAnnounce] = useState('');
  const [pollExpired, setPollExpired] = useState(false);
  // A cancelled return is captured into state so the reassurance survives the URL param being
  // stripped (below) — otherwise the notice would vanish on the very next render.
  const [cancelledNotice] = useState(checkoutReturn === 'cancelled');
  const wasLockedRef = useRef<boolean | null>(null);

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['reference', id],
    queryFn: () => apiClient.getReference(card),
    // Returning from a paid checkout, the webhook grants the unlock server-side and may lag a beat;
    // poll until it lands (bounded by the pollExpired timer below), then stop.
    refetchInterval: (query) => {
      const current = query.state.data;
      if (!awaitingWebhook || pollExpired || current === undefined || current.unlocked) return false;
      return 1500;
    },
  });

  // Detect the locked → unlocked transition and play the ceremony once (+ announce for a11y). An
  // already-unlocked FIRST load (a seed card, a reload) is the baseline, not a transition, so no
  // ceremony fires for it.
  useEffect(() => {
    if (!data) return;
    const nowLocked = !data.unlocked;
    if (wasLockedRef.current === true && !nowLocked) {
      setSealing(true);
      setAnnounce('Unlocked. Your letter is ready to read.');
    }
    wasLockedRef.current = nowLocked;
  }, [data]);

  // Bound the post-checkout webhook wait so a lagging grant shows a calm "refresh to check" fallback
  // rather than an endless spinner.
  useEffect(() => {
    if (!awaitingWebhook) return;
    const timer = setTimeout(() => setPollExpired(true), 15_000);
    return () => clearTimeout(timer);
  }, [awaitingWebhook]);

  // Once the checkout-return has been handled (reconciled to unlocked, or a cancel shown), strip the
  // param + clear the pending stash so a reload doesn't replay the return handling.
  useEffect(() => {
    if (checkoutReturn === null) return;
    if (checkoutReturn === 'cancelled' || (data?.unlocked ?? false)) {
      clearPendingUnlock();
      const next = new URLSearchParams(searchParams);
      next.delete('checkout');
      setSearchParams(next, { replace: true });
    }
  }, [checkoutReturn, data?.unlocked, searchParams, setSearchParams]);

  // The PDF export: get the presigned URL, fetch it to a Blob, and save it as a real file. A
  // still-rendering PDF (`pending`) is auto-polled a few times before falling back to a manual retry.
  const pdfFilename = `reference-${card.refereeName.replace(/\s+/g, '-').toLowerCase()}.pdf`;
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollAttempts = useRef(0);
  const retryExportRef = useRef<() => void>(() => {});
  useEffect(() => {
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, []);

  const pdf = useMutation({
    mutationFn: async (): Promise<{ kind: 'ready'; blob: Blob } | { kind: 'pending' }> => {
      const download = await apiClient.getLetterPdf(id);
      if (download.status !== 'ready' || !download.downloadUrl) return { kind: 'pending' };
      // The presigned URL is a ~5-min R2 GET; fetch it so we can save a named file (a cross-origin
      // link ignores the anchor `download` attr, but the object URL minted from this Blob honours it).
      const blob = await fetchPdfBlob(download.downloadUrl);
      return { kind: 'ready', blob };
    },
    onSuccess: (result) => {
      if (result.kind === 'ready') {
        pollAttempts.current = 0;
        setPdfNotice(null);
        saveBlob(result.blob, pdfFilename);
        return;
      }
      // Unlocked, but the post-sign render is still in flight — reassure and auto-poll (never an error).
      if (pollAttempts.current < 3) {
        pollAttempts.current += 1;
        setPdfNotice('Your PDF is being prepared — hang tight, this only takes a moment.');
        pollTimer.current = setTimeout(() => retryExportRef.current(), 2500);
      } else {
        setPdfNotice('Still preparing — tap Export PDF to check again.');
      }
    },
    onError: (err) => {
      setPdfNotice(null);
      if (err instanceof CandidateApiError && err.kind === 'forbidden') {
        // The entitlement is gone (a rare race for a viewer that only renders when unlocked): send the
        // candidate back into the unlock flow by re-reading the reference (it re-renders as the panel).
        void queryClient.invalidateQueries({ queryKey: ['reference', id] });
        return;
      }
      setPdfError('We couldn’t download the PDF just now. Try again in a moment.');
    },
  });
  // Kept fresh so the pending-poll timer re-runs the latest mutation without a use-before-define cycle.
  useEffect(() => {
    retryExportRef.current = () => pdf.mutate();
  });

  /** Start a fresh export (the Export PDF button): reset the poll budget + any prior message. */
  function startPdfExport() {
    pollAttempts.current = 0;
    if (pollTimer.current) clearTimeout(pollTimer.current);
    setPdfError(null);
    setPdfNotice(null);
    pdf.mutate();
  }

  async function share(referenceId: string) {
    const text = `A reference for me, verified by By My Word · Ref ${referenceId}`;
    const nav = navigator as Navigator & { share?: (data: { text: string }) => Promise<void> };
    try {
      if (typeof nav.share === 'function') await nav.share({ text });
      else await navigator.clipboard?.writeText(text);
    } catch {
      /* user dismissed the share sheet — nothing to do */
    }
  }

  /** A one-tap / reconciled unlock: refresh this reference (reveals the letter + triggers the
   * ceremony via the transition effect) and the ledger + detail so the card reflects it. */
  function handleUnlocked() {
    void queryClient.invalidateQueries({ queryKey: ['reference', id] });
    void queryClient.invalidateQueries({ queryKey: ['ledger'] });
    void queryClient.invalidateQueries({ queryKey: ['request', id] });
  }

  const announcer = (
    <p className="sr-only" role="status" aria-live="polite">
      {announce}
    </p>
  );

  if (isPending) {
    return (
      <div className="mt-6 flex justify-center py-10" aria-live="polite" aria-busy="true">
        <ThreadMark className="brand-pulse h-5 w-auto" />
      </div>
    );
  }
  if (isError) {
    // Degrade calmly rather than dead-ending (a transient content/unlock read failure).
    return (
      <section className="mt-6 rounded-lg border border-line bg-surface p-5 text-center">
        <p className="font-body text-[13.5px] leading-relaxed text-ink-quiet">
          The highlights and the letter open here shortly.
        </p>
        <Button variant="ghost" className="mt-3" onClick={() => void refetch()}>
          Try again
        </Button>
      </section>
    );
  }

  if (data.unlocked) {
    return (
      <div className="mt-5">
        {announcer}
        {data.letter ? (
          <LetterViewer
            content={data}
            sealing={sealing}
            pdfPending={pdf.isPending}
            pdfError={pdfError}
            pdfNotice={pdfNotice}
            onExportPdf={startPdfExport}
            onShare={() => void share(data.referenceId)}
          />
        ) : (
          <LetterPending />
        )}
      </div>
    );
  }

  // Locked. While a paid checkout is settling, show the confirming state instead of re-offering the
  // purchase (avoids a double charge); otherwise the full unlock panel.
  return (
    <div className="mt-5">
      {announcer}
      {cancelledNotice && <CheckoutCancelledNotice />}
      {awaitingWebhook ? (
        <ConfirmingPayment slow={pollExpired} onRefresh={() => void refetch()} />
      ) : (
        <UnlockPanel content={data} onUnlocked={handleUnlocked} />
      )}
    </div>
  );
}

export function RequestDetail() {
  const { id = '' } = useParams();
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['request', id],
    queryFn: () => apiClient.getRequestDetail(id),
    retry: (count, err) => !(err instanceof CandidateApiError && err.kind === 'not_found') && count < 1,
  });

  if (isPending) return <DetailLoading />;
  if (isError) {
    const notFound = error instanceof CandidateApiError && error.kind === 'not_found';
    return <DetailError notFound={notFound} onRetry={() => void refetch()} />;
  }

  const { card, personalNote, timeline } = data;
  const state = card.state;

  return (
    <AppShell title="Reference">
      <BackLink />
      <RefereeHeader card={card} />

      {(state === 'ready' || state === 'unlocked') && <ReferenceSurface id={id} card={card} />}
      {state === 'declined' && <DeclinedBranch card={card} />}
      {state === 'expired' && <ExpiredBranch card={card} />}

      <section className="mt-6">
        <h2 className="font-display text-[13px] font-semibold uppercase tracking-[0.06em] text-ink-quiet">
          {state === 'unlocked' ? 'How it came together' : 'Progress'}
        </h2>
        <div className="mt-3">
          <StatusTimeline timeline={timeline} refereeName={card.refereeName} />
        </div>
      </section>

      {personalNote && (
        <section className="mt-5 rounded-lg border border-line bg-surface-tint px-4 py-3">
          <p className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-quiet">The note you sent</p>
          <p className="mt-1 font-body text-[13.5px] italic leading-relaxed text-ink">“{personalNote}”</p>
        </section>
      )}

      {(state === 'sent' || state === 'opened') && (
        <RequestActions card={card} onChanged={() => void refetch()} />
      )}
    </AppShell>
  );
}
